import React, { useState } from "react";
import { DocumentIcon } from "../icons";
import FileRow from "./FileRow";

interface FileItem {
  id: string;
  displayName: string;
  description?: string;
  originalFileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  category: string;
  defaultNote?: string;
  metadata?: {
    mimeType?: string;
    tags?: string[];
  };
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface FileFilters {
  includeArchived: boolean;
  category: string | null;
  fileType: string | null;
}

interface FilesTableProps {
  files: FileItem[];
  isLoading: boolean;
  filters: FileFilters;
  onRefresh: () => void;
}

const FilesTable: React.FC<FilesTableProps> = ({
  files,
  isLoading,
  filters,
  onRefresh,
}) => {
  const [sortField, setSortField] = useState<keyof FileItem>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (field: keyof FileItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileTypeIcon = (fileType: string) => {
    return <DocumentIcon className="h-5 w-5 text-gray-400" />;
  };

  const getCategoryLabel = (category: string): string => {
    const categories = {
      offer_attachment: "Załącznik do oferty",
      brand_asset: "Materiał brandowy",
      other: "Inne",
    };
    return categories[category as keyof typeof categories] || category;
  };

  const sortedFiles = [...files].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const filteredFiles = sortedFiles.filter((file) => {
    if (!filters.includeArchived && file.isArchived) return false;
    if (filters.category && file.category !== filters.category) return false;
    if (filters.fileType) {
      const fileExt = file.originalFileName.toLowerCase();
      switch (filters.fileType) {
        case "pdf":
          return fileExt.endsWith(".pdf");
        case "excel":
          return fileExt.endsWith(".xls") || fileExt.endsWith(".xlsx");
        case "word":
          return fileExt.endsWith(".doc") || fileExt.endsWith(".docx");
        default:
          return true;
      }
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">Ładowanie plików...</p>
      </div>
    );
  }

  if (filteredFiles.length === 0) {
    return (
      <div className="p-8 text-center">
        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Brak plików</h3>
        <p className="mt-1 text-sm text-gray-500">
          {files.length === 0
            ? "Nie masz jeszcze żadnych plików. Prześlij pierwszy plik powyżej."
            : "Żadne pliki nie spełniają aktualnych kryteriów filtrowania."}
        </p>
        {files.length > 0 && (
          <button
            onClick={onRefresh}
            className="mt-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Odśwież
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">
            Pliki ({filteredFiles.length})
          </h3>
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Odśwież
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="text-left">
                  <button
                    onClick={() => handleSort("displayName")}
                    className="group inline-flex items-center hover:text-gray-700 text-left"
                  >
                    Nazwa
                    <span className="ml-1 flex-none rounded text-gray-400 group-hover:text-gray-500">
                      {sortField === "displayName"
                        ? sortDirection === "asc"
                          ? "↑"
                          : "↓"
                        : "↕"}
                    </span>
                  </button>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Typ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort("fileSize")}
                  className="group inline-flex items-center hover:text-gray-700"
                >
                  Rozmiar
                  <span className="ml-1 flex-none rounded text-gray-400 group-hover:text-gray-500">
                    {sortField === "fileSize"
                      ? sortDirection === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </span>
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kategoria
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Autor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort("createdAt")}
                  className="group inline-flex items-center hover:text-gray-700"
                >
                  Data
                  <span className="ml-1 flex-none rounded text-gray-400 group-hover:text-gray-500">
                    {sortField === "createdAt"
                      ? sortDirection === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </span>
                </button>
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Akcje</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredFiles.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                onRefresh={onRefresh}
                getFileTypeIcon={getFileTypeIcon}
                getCategoryLabel={getCategoryLabel}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FilesTable;
